<script setup lang="ts">
import type { IGalleryFilters, MediaTypeFilter } from "@/components/GalleryFiltersDrawer/galleryFilters.interface"
import type { ISelectItem } from "@/types"
import { computed, onMounted, reactive } from "vue"
import { useListTags } from "@/api"
import { Combobox, Drawer, Select, Switch, TagInput } from "@/components/ui"
import { useTranslation } from "@/composables"
import { GalleryFiltersDrawerKey } from "@/symbols"

const props = withDefaults(
  defineProps<{
    filters?: IGalleryFilters
    showByDefault?: boolean
    eventVaultId?: number
    tagsEnabled?: boolean
    reactionsEnabled?: boolean
  }>(),
  { showByDefault: false, filters: undefined, eventVaultId: undefined, tagsEnabled: false, reactionsEnabled: false },
)

const emit = defineEmits<{
  (e: "update:filters", filters: IGalleryFilters): void
}>()

const { t } = useTranslation()

const mediaTypeDefaults: MediaTypeFilter[] = ["pics", "vids"]

const filterDefaults = {
  perPage: 50,
  orderBy: undefined,
  sortBy: undefined,
  mediaType: mediaTypeDefaults,
  showGuestUploadsOnly: false,
  showPinnedUploadsOnly: false,
  tags: [],
}

const internalFilterState = reactive<IGalleryFilters>(structuredClone(filterDefaults))

onMounted(() => {
  if (props.filters?.perPage)
    internalFilterState.perPage = props.filters.perPage
  if (props.filters?.orderBy)
    internalFilterState.orderBy = props.filters.orderBy
  if (props.filters?.sortBy)
    internalFilterState.sortBy = props.filters.sortBy
  if (props.filters?.mediaType?.length)
    internalFilterState.mediaType = props.filters.mediaType
  if (props.filters?.showGuestUploadsOnly)
    internalFilterState.showGuestUploadsOnly = props.filters.showGuestUploadsOnly
  if (props.filters?.showPinnedUploadsOnly)
    internalFilterState.showPinnedUploadsOnly = props.filters.showPinnedUploadsOnly
})

const perPageOptions = [
  { label: "5", value: 5 },
  { label: "10", value: 10 },
  { label: "15", value: 15 },
  { label: "25", value: 25 },
  { label: "50", value: 50 },
  { label: "100", value: 100 },
]

const reactionsEnabled = computed(() => props.reactionsEnabled)

const orderByOptions = computed<ISelectItem[]>(() => {
  if (props.filters?.sortBy) {
    if (["Type", "Title", "GuestName"].includes(props.filters.sortBy)) {
      return [
        { label: t("ascending"), value: "asc" },
        { label: t("descending"), value: "desc" },
      ]
    }
    else if (reactionsEnabled.value && props.filters.sortBy === "Popularity") {
      return [
        { label: t("least_to_most"), value: "asc" },
        { label: t("most_to_least"), value: "desc" },
      ]
    }
  }
  return [
    { label: t("oldest_first"), value: "asc" },
    { label: t("newest_first"), value: "desc" },
  ]
})

const sortByOptions = computed<ISelectItem[]>(() => {
  const options = [
    { label: t("type"), value: "Type" },
    { label: t("date_uploaded"), value: "DateUploaded" },
    { label: t("date_taken"), value: "DateTaken" },
    { label: t("title"), value: "Title" },
    { label: t("uploaded_by"), value: "GuestName" },
  ]
  if (reactionsEnabled.value) {
    options.push({ label: t("popularity"), value: "Popularity" })
  }
  return options
})

const mediaTypeOptions = computed<ISelectItem<MediaTypeFilter>[]>(() => [
  { label: t("images"), value: "pics" },
  { label: t("videos"), value: "vids" },
  { label: t("guestbook_videos"), value: "guestbookVids" },
])

const selectedMediaTypes = computed<ISelectItem<MediaTypeFilter>[]>({
  get: () => mediaTypeOptions.value.filter(option => internalFilterState.mediaType?.includes(option.value)),
  set: (value) => {
    internalFilterState.mediaType = value.map(option => option.value)
  },
})

const tagsEnabled = computed(() => props.tagsEnabled)

const { data: allEventTags } = useListTags(props.eventVaultId, { enabled: tagsEnabled })
</script>

<template>
  <Drawer :name="GalleryFiltersDrawerKey" :title="t('filters')" :show-by-default="showByDefault">
    <div class="gpx-gallery-page-filters">
      <Switch
        id="showGuestUploadsOnly"
        v-model="internalFilterState.showGuestUploadsOnly"
        name="showGuestUploadsOnly"
        :label="t('show_my_uploads')"
        @update:model-value="emit('update:filters', internalFilterState)"
      />
      <Switch
        id="showPinnedUploadsOnly"
        v-model="internalFilterState.showPinnedUploadsOnly"
        name="showPinnedUploadsOnly"
        :label="t('show_host_favorites')"
        @update:model-value="emit('update:filters', internalFilterState)"
      />
      <Select
        v-model="internalFilterState.perPage"
        name="perPage"
        :label="t('items_per_page')"
        :items="perPageOptions"
        @update:model-value="emit('update:filters', internalFilterState)"
      />
      <Select
        v-model="internalFilterState.sortBy"
        name="sortBy"
        :label="t('sort_by')"
        :items="sortByOptions"
        :placeholder="t('select_option')"
        clearable
        @clear="emit('update:filters', internalFilterState)"
        @update:model-value="emit('update:filters', internalFilterState)"
      />
      <Select
        v-model="internalFilterState.orderBy"
        name="orderBy"
        :label="t('order_by')"
        :items="orderByOptions"
        :placeholder="t('select_option')"
        clearable
        @clear="emit('update:filters', internalFilterState)"
        @update:model-value="emit('update:filters', internalFilterState)"
      />
      <Combobox
        v-model="selectedMediaTypes"
        name="mediaType"
        :label="t('media_type')"
        :items="mediaTypeOptions"
        multiple
        @update:model-value="emit('update:filters', internalFilterState)"
      />
      <TagInput
        v-if="tagsEnabled && allEventTags?.length"
        v-model="internalFilterState.tags"
        search-only
        :existing-tags="allEventTags ?? []"
        :label="t('gallery_filters_drawer.tags_label')"
        :placeholder="t('gallery_filters_drawer.tags_placeholder')"
        @add-tag="emit('update:filters', internalFilterState)"
        @remove-tag="emit('update:filters', internalFilterState)"
      />
    </div>
  </Drawer>
</template>

<style scoped>
.gpx-gallery-page-filters {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
</style>
